import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, Overline, Spinner, Empty } from "@/components/Widgets";
import { PenNib, Sparkle, Copy, FloppyDisk, Trash, Clock } from "@phosphor-icons/react";
import { PageGuide } from "@/components/PageGuide";

const PLATFORMS = ["Instagram", "X (Twitter)", "TikTok", "YouTube", "LinkedIn", "Facebook", "Threads"];
const TYPES = ["post", "caption", "bio", "thread", "video script"];
const TONES = ["authentic", "inspirational", "professional", "playful", "bold", "luxury"];

export default function ContentStudio() {
  const [form, setForm] = useState({ platform: "Instagram", content_type: "post", topic: "", tone: "authentic" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [saved, setSaved] = useState([]);

  const loadSaved = () => api.get("/content").then(({ data }) => setSaved(data)).catch(() => {});
  useEffect(() => { loadSaved(); }, []);

  const generate = async (e) => {
    e.preventDefault();
    if (!form.topic.trim()) return toast.error("Enter a topic");
    setBusy(true);
    try {
      const { data } = await api.post("/content/generate", form);
      setResult(data);
      toast.success("Content generated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const save = async (v) => {
    try {
      await api.post("/content/save", {
        platform: form.platform, content_type: form.content_type, topic: form.topic,
        body: v.body, hashtags: v.hashtags || [],
      });
      loadSaved();
      toast.success("Saved to library");
    } catch (err) { toast.error("Save failed"); }
  };

  const remove = async (id) => {
    await api.delete(`/content/${id}`);
    loadSaved();
    toast.success("Deleted");
  };

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Copied"); };

  return (
    <div className="space-y-6 fade-up">
      <PageGuide title="How the Content Studio works" steps={[
        "Choose a platform, content type and tone, then describe your topic.",
        "Click Generate content to get 3 platform-ready variations with hashtags.",
        "Copy a variation to post it, or Save it to your content library.",
        "Reuse or delete saved posts anytime from the library below.",
      ]} />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-1 h-fit" testid="content-form-card">
          <div className="flex items-center gap-2 mb-4">
            <PenNib size={22} weight="bold" className="text-primary" />
            <h3 className="font-head font-bold text-lg">Content Generator</h3>
          </div>
          <form onSubmit={generate} className="space-y-4">
            <Select label="Platform" value={form.platform} options={PLATFORMS} onChange={(v) => setForm({ ...form, platform: v })} testid="content-platform" />
            <Select label="Type" value={form.content_type} options={TYPES} onChange={(v) => setForm({ ...form, content_type: v })} testid="content-type" />
            <Select label="Tone" value={form.tone} options={TONES} onChange={(v) => setForm({ ...form, tone: v })} testid="content-tone" />
            <div>
              <label className="overline text-muted-foreground block mb-2">Topic / brief</label>
              <textarea
                value={form.topic}
                data-testid="content-topic"
                onChange={(e) => setForm({ ...form, topic: e.target.value })}
                placeholder="Launching my new sustainable clothing line…"
                rows={4}
                className="w-full bg-secondary border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-primary transition-colors duration-200 resize-none"
              />
            </div>
            <button type="submit" disabled={busy} data-testid="generate-content-btn"
              className="w-full bg-primary text-primary-foreground font-semibold font-semibold py-3 text-sm flex items-center justify-center gap-2 hover:bg-primary/90 transition-colors duration-200 disabled:opacity-50">
              {busy ? <Spinner label="Writing…" /> : (<><Sparkle size={18} weight="fill" /> Generate content</>)}
            </button>
          </form>
        </Card>

        <div className="lg:col-span-2 space-y-4">
          {!result && !busy && <Empty>Generate viral, positive, on-brand content across every platform.</Empty>}
          {busy && !result && <Card><div className="py-12 flex justify-center"><Spinner label="Crafting variations…" /></div></Card>}
          {result && (
            <>
              <div className="flex gap-4 flex-wrap">
                {result.best_time_to_post && <Info icon={Clock} label="Best time" value={result.best_time_to_post} />}
                {result.cta && <Info label="Suggested CTA" value={result.cta} />}
              </div>
              {result.variations?.map((v, i) => (
                <Card key={i} testid={`variation-${i}`}>
                  <div className="flex items-center justify-between mb-3">
                    <Overline>VARIATION {String.fromCharCode(65 + i)}</Overline>
                    <div className="flex gap-1">
                      <IconBtn icon={Copy} onClick={() => copy(v.body)} testid={`copy-${i}`} />
                      <IconBtn icon={FloppyDisk} onClick={() => save(v)} testid={`save-${i}`} />
                    </div>
                  </div>
                  <p className="text-sm whitespace-pre-wrap leading-relaxed">{v.body}</p>
                  {v.hashtags?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {v.hashtags.map((h, j) => (
                        <span key={j} className="font-mono text-xs text-primary">{h.startsWith("#") ? h : `#${h}`}</span>
                      ))}
                    </div>
                  )}
                </Card>
              ))}
              {result.seo_note && (
                <div className="border border-border p-3 text-xs text-muted-foreground font-mono">SEO TIP · {result.seo_note}</div>
              )}
            </>
          )}
        </div>
      </div>

      {saved.length > 0 && (
        <div>
          <Overline className="mb-3">CONTENT LIBRARY</Overline>
          <div className="grid md:grid-cols-2 gap-4">
            {saved.map((s) => (
              <Card key={s.id} testid={`saved-${s.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <Overline>{s.platform} · {s.content_type}</Overline>
                  <IconBtn icon={Trash} onClick={() => remove(s.id)} testid={`delete-${s.id}`} tone="accent" />
                </div>
                <p className="text-sm text-muted-foreground line-clamp-3">{s.body}</p>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Select({ label, value, options, onChange, testid }) {
  return (
    <div>
      <label className="overline text-muted-foreground block mb-2">{label}</label>
      <select value={value} data-testid={testid} onChange={(e) => onChange(e.target.value)}
        className="w-full bg-secondary border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-primary transition-colors duration-200 capitalize">
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
function IconBtn({ icon: Icon, onClick, testid, tone }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`p-1.5 border border-border hover:bg-secondary transition-colors duration-200 ${tone === "accent" ? "hover:text-destructive" : "hover:text-primary"}`}>
      <Icon size={16} weight="bold" />
    </button>
  );
}
function Info({ icon: Icon, label, value }) {
  return (
    <div className="border border-border px-3 py-2 flex items-center gap-2">
      {Icon && <Icon size={16} className="text-primary" />}
      <div><div className="overline text-muted-foreground">{label}</div><div className="text-sm">{value}</div></div>
    </div>
  );
}
